import React, { FC, useCallback } from "react";
import {
  FormRenderProps,
  useForm,
  useFormState,
} from "react-final-form";
import { Grid, makeStyles } from "@material-ui/core";
import PrivateClassDescription from "./PrivateClassDescription";
import { Language } from "../../../language-course/modals/components";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import { IFile } from "../../../../components/UploadFile/types";
import usePrivateClassQueries from "../../hooks/usePrivateClassQueries";
import { useNotify } from "../../../notify/useNotify";
import { mutateErrors } from "../../../../utils";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import PrivateClassSubjectField from "../../components/PrivateClassSubjectField";

const useStyles = makeStyles(() => ({
  mainContainer: {
    width: "100%",
  },
  title: {
    marginTop: 8,
    marginBottom: 16,
    fontSize: 18,
    fontWeight: "bold",
    color: "#535358",
  },
}));

const Content: FC<FormRenderProps> = ({ handleSubmit, errors }) => {
  const classes = useStyles();
  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const { uploadExtraFile } = usePrivateClassQueries();
  const snackNotify = useNotify();

  const extraFiles: IFile[] = values?.extraFiles ?? [];

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadExtraFile(file);
          push("extraFiles", data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [uploadExtraFile, push, snackNotify],
  );

  const removeFileHandler = useCallback(
    (file) => {
      const index = extraFiles.indexOf(file);
      remove("extraFiles", index);
    },
    [extraFiles, remove],
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className={classes.mainContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <PrivateClassSubjectField
              name="subjectId"
              placeholder="Select subject"
              fullWidth
              hasError={!!errors?.subjectId}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Language
              label="Teaching Language"
              fieldName="teachingLanguage"
              errors={errors?.teachingLanguage}
            />
          </Grid>
          <PrivateClassDescription />
          <Grid item xs={12}>
            <UploadFile
              uploadedFiles={extraFiles}
              addNewFiles={addNewFiles}
              removeFile={removeFileHandler}
              infoText={"You can upload extra files for this private class."}
              titleText={"Upload Extra Files"}
              allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
              maxSize={5}
              maxFiles={5}
            />
          </Grid>
        </Grid>
      </div>
    </form>
  );
};

export default Content;
